import { useEffect, useRef, useState } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { MapPin } from "lucide-react";

const esc = (s) => String(s ?? "").replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));

export default function MapTimeline({ events, t }) {
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const markers = useRef({});
  const [active, setActive] = useState(null);
  const geo = events.filter((e) => e.lat != null && e.lng != null && !Number.isNaN(Number(e.lat)));

  useEffect(() => {
    if (!containerRef.current) return;
    if (mapRef.current) { mapRef.current.remove(); mapRef.current = null; markers.current = {}; }
    const map = L.map(containerRef.current, { zoomControl: true, scrollWheelZoom: false }).setView([20, 0], 2);
    const tiles = t.dark ? "dark_all" : "light_all";
    L.tileLayer(`https://{s}.basemaps.cartocdn.com/${tiles}/{z}/{x}/{y}{r}.png`, { attribution: "© OpenStreetMap © CARTO", maxZoom: 19 }).addTo(map);
    mapRef.current = map;
    geo.forEach((e) => {
      const icon = L.divIcon({ className: "", iconSize: [18, 18], iconAnchor: [9, 9],
        html: `<span style="display:block;width:18px;height:18px;border-radius:9999px;background:${e.color || t.dot};box-shadow:0 0 0 4px ${t.bg},0 2px 6px rgba(0,0,0,0.4);cursor:pointer"></span>` });
      const m = L.marker([Number(e.lat), Number(e.lng)], { icon }).addTo(map).bindPopup(`<b>${esc(e.title)}</b><br/>${esc(e.date)}`);
      m.on("click", () => setActive(e.id));
      markers.current[e.id] = m;
    });
    if (geo.length) { try { map.fitBounds(geo.map((e) => [Number(e.lat), Number(e.lng)]), { padding: [50, 50], maxZoom: 6 }); } catch {} }
    const to = setTimeout(() => map.invalidateSize(), 250);
    return () => { clearTimeout(to); map.remove(); mapRef.current = null; markers.current = {}; };
    // eslint-disable-next-line
  }, [t.dark, geo.length]);

  const fly = (e) => {
    setActive(e.id);
    const m = markers.current[e.id];
    if (m && mapRef.current) { mapRef.current.flyTo([Number(e.lat), Number(e.lng)], 6, { duration: 1.1 }); m.openPopup(); }
  };

  return (
    <div className="max-w-6xl mx-auto px-5 pb-24 pt-2 grid md:grid-cols-[320px_1fr] gap-6">
      <div className="space-y-2 md:max-h-[72vh] overflow-y-auto no-scrollbar order-2 md:order-1">
        {events.map((e) => {
          const has = e.lat != null && e.lng != null && !Number.isNaN(Number(e.lat));
          return (
            <button key={e.id} onClick={() => has && fly(e)} data-testid={`map-event-${e.id}`}
              className={`w-full text-left rounded-xl border p-3.5 transition-colors ${active === e.id ? "" : "hover:opacity-90"}`}
              style={{ borderColor: active === e.id ? t.accent : t.surfaceBorder, background: active === e.id ? `${t.accent}18` : t.surface }}>
              <div className="text-[11px] font-semibold uppercase tracking-widest" style={{ color: t.accent }}>{e.date}</div>
              <div className="font-medium mt-0.5" style={{ fontFamily: t.heading, color: t.text }}>{e.title}</div>
              <div className="flex items-center gap-1 mt-1 text-xs" style={{ color: t.muted }}>
                <MapPin size={12} /> {e.location || (has ? `${Number(e.lat).toFixed(2)}, ${Number(e.lng).toFixed(2)}` : "No location set")}
              </div>
            </button>
          );
        })}
      </div>
      <div className="order-1 md:order-2">
        <div ref={containerRef} data-testid="map-canvas" className="rounded-2xl overflow-hidden border h-[52vh] md:h-[72vh]" style={{ borderColor: t.surfaceBorder }} />
        {geo.length === 0 && <p className="mt-3 text-sm text-center" style={{ color: t.muted }}>Add latitude & longitude to events to place them on the map.</p>}
      </div>
    </div>
  );
}
